import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { useCompany } from '@/contexts/CompanyContext';
import { useTodos, useAllTodos } from '@/hooks/useTodos';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { ConsultantTodoList } from '@/components/todos/ConsultantTodoList';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';

// FIN-578 PR1 — read-only consultant review page (F9). Lists every open item, any
// audience, for the active company. "Send to client" wiring lands in PR2/PR3.
//
// FIN-876 — in Platform Admin View (platform_admin with no active company) the same route
// lists internal todos across every company instead, matching how /review,
// /unmatched-emails and /messages already behave. The mode is decided here, not by a
// separate route, so the two views cannot drift apart.
//
// FIN-878 — the admin list opens internal-only (the safe resting state) with an opt-in for
// client-visible rows. The backend already returns both audiences, so revealing is a filter
// over the payload already fetched: no refetch, no loading flicker, and one cache entry for
// a redirect to invalidate rather than two.
export default function Todos() {
  const t = useTranslations('consultantTodos.page');
  const { company, loading: companyLoading, isPlatformAdminView } = useCompany();
  // Session-only by design. A reload returning to internal-only is the safe direction:
  // forgetting that client rows were revealed hides less than it would wrongly expose.
  const [showClientAudience, setShowClientAudience] = useState(false);

  const singleCompany = useTodos(isPlatformAdminView ? undefined : company?.id);
  const crossCompany = useAllTodos(isPlatformAdminView);
  const { data: fetchedTodos, isLoading, isError } = isPlatformAdminView ? crossCompany : singleCompany;

  const todos =
    isPlatformAdminView && !showClientAudience
      ? (fetchedTodos ?? []).filter((todo) => todo.audience === 'internal')
      : fetchedTodos;

  // platform-admin-cross-client-todos.Q6 — volume is stated rather than capped. Nothing is
  // truncated; a worklist that silently hides rows is worse than a long one. Counted from
  // what is rendered, so the figure always matches the list under the toggle.
  const crossCompanyCount = todos?.length ?? 0;
  const crossCompanyCompanies = new Set((todos ?? []).map((todo) => todo.company_id)).size;

  const singleCompanySubtitle = company?.name
    ? t('subtitleCompany', { company: company.name })
    : t('subtitleNoCompany');
  const adminSubtitle = showClientAudience ? t('subtitleAdminAll') : t('subtitleAdminInternal');
  const subtitle = isPlatformAdminView ? adminSubtitle : singleCompanySubtitle;

  // Names what is actually empty: with client rows revealed, "no internal todos" would answer
  // a narrower question than the one the admin just asked.
  let adminEmptyMessage: string | undefined;
  if (isPlatformAdminView) {
    adminEmptyMessage = showClientAudience ? t('emptyAdminAll') : t('emptyAdminInternal');
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6 p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="page-heading">{t('title')}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">{subtitle}</p>
          {isPlatformAdminView && !isLoading && !isError && crossCompanyCount > 0 && (
            <p className="mt-1 text-xs text-muted-foreground" data-testid="cross-company-count">
              {t('count', { todos: crossCompanyCount, companies: crossCompanyCompanies })}
          </p>
          )}
        </div>
        {isPlatformAdminView && (
          <div className="flex flex-none items-center gap-2">
            <Switch
              id="show-client-audience"
              checked={showClientAudience}
              onCheckedChange={setShowClientAudience}
            />
            <Label htmlFor="show-client-audience" className="cursor-pointer text-sm font-normal">
              {t('showClient')}
            </Label>
          </div>
        )}
      </div>

      {(companyLoading || isLoading) && (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertTitle>{t('loadFailedTitle')}</AlertTitle>
          <AlertDescription>{t('loadFailedBody')}</AlertDescription>
        </Alert>
      )}

      {!companyLoading && !isLoading && !isError && (
        <ConsultantTodoList
          todos={todos ?? []}
          showCompanyLabel={isPlatformAdminView}
          emptyMessage={adminEmptyMessage}
        />
      )}
    </div>
  );
}
